import React from 'react';
import PropTypes from 'prop-types';
import {
  WaveSmooth,
  WaveLayered,
  WaveAsymmetric,
  Tilt,
  Arrow,
  CurveConcave,
  CurveConvex,
  CurveDouble,
  Sweep,
  Spill,
  Triangle,
  Peaks,
  Steps,
  Fade
} from './presets';

const PRESET_COMPONENTS = {
  'wave-smooth': WaveSmooth,
  'wave-layered': WaveLayered,
  'wave-asymmetric': WaveAsymmetric,
  'tilt': Tilt,
  'arrow': Arrow,
  'curve-concave': CurveConcave,
  'curve-convex': CurveConvex,
  'curve-double': CurveDouble,
  'sweep': Sweep,
  'spill': Spill,
  'triangle': Triangle,
  'peaks': Peaks,
  'steps': Steps,
  'fade': Fade
};

// Base styles for the divider container
const dividerStyles = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  width: '100%',
  overflow: 'hidden',
  lineHeight: 0,
  pointerEvents: 'none',
  zIndex: 1,
  transform: 'translateY(1px)' // Prevent sub-pixel gap
};

// Style for SVG to fill container
const svgStyle = {
  display: 'block',
  width: '100%',
  height: '100%'
};

// Preset-specific height overrides
const PRESET_HEIGHTS = {
  'wave-smooth': 'clamp(40px, 5vw, 80px)',
  'wave-layered': 'clamp(60px, 7vw, 100px)',
  'wave-asymmetric': 'clamp(40px, 5vw, 80px)',
  'tilt': 'clamp(40px, 5vw, 80px)',
  'arrow': 'clamp(40px, 5vw, 80px)',
  'curve-concave': 'clamp(40px, 5vw, 80px)',
  'curve-convex': 'clamp(40px, 5vw, 80px)',
  'curve-double': 'clamp(40px, 5vw, 80px)',
  'sweep': 'clamp(40px, 5vw, 80px)',
  'spill': 'clamp(60px, 8vw, 120px)',
  'triangle': 'clamp(40px, 5vw, 80px)',
  'peaks': 'clamp(60px, 10vw, 160px)',
  'steps': 'clamp(60px, 10vw, 160px)',
  'fade': 'clamp(60px, 8vw, 120px)'
};

export function Divider({
  preset,
  steepness,
  amplitude,
  depth,
  position,
  softness,
  fadeOpacity,
  fadeDirection,
  fadeMidpoint,
  className,
  height: heightProp
}) {
  const PresetComponent = PRESET_COMPONENTS[preset];

  if (!PresetComponent) {
    return null;
  }

  const presetHeight = PRESET_HEIGHTS[preset] || 'clamp(40px, 5vw, 80px)';
  const containerStyle = {
    ...dividerStyles,
    height: heightProp || presetHeight
  };

  return (
    <div className={ className } style={ containerStyle }>
      <PresetComponent
        steepness={ steepness }
        amplitude={ amplitude }
        depth={ depth }
        position={ position }
        softness={ softness }
        fadeOpacity={ fadeOpacity }
        fadeDirection={ fadeDirection }
        fadeMidpoint={ fadeMidpoint }
        style={ svgStyle }
      />
    </div>
  );
}

Divider.propTypes = {
  preset: PropTypes.oneOf([
    'wave-smooth',
    'wave-layered',
    'wave-asymmetric',
    'tilt',
    'arrow',
    'curve-concave',
    'curve-convex',
    'curve-double',
    'sweep',
    'spill',
    'triangle',
    'peaks',
    'steps',
    'fade'
  ]).isRequired,
  steepness: PropTypes.number,
  amplitude: PropTypes.number,
  depth: PropTypes.number,
  position: PropTypes.number,
  softness: PropTypes.number,
  fadeOpacity: PropTypes.number,
  fadeDirection: PropTypes.oneOf(['down', 'up', 'left', 'right']),
  fadeMidpoint: PropTypes.number,
  className: PropTypes.string,
  height: PropTypes.string
};

export default Divider;
